import styled from "styled-components";
import ToggleInput from "./ToggleInput";
import { Button } from "@te-whatu-ora/anatomic";
import { PatientType, PracticeType } from "@context/antibioticContext";
import { useContext, useState } from "react";
import { Patient, Practice } from "@constants/antibiotics";
import mediaQuery from "@utils/mediaQuery";
import { OnboardingContext } from "@context/onboardingContext";
import { getInferredPreferences } from "@utils/onboardingPreferenceUtil";

interface Props {
  onClick: (practice: PracticeType, patient: PatientType) => void;
  practice: PracticeType;
  patient: PatientType;
}

const TitleContainer = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  margin-bottom: 12px;
`;

const SubTitle = styled.div`
  color: #000;
  font-size: var(--font-size-xl);
  font-style: normal;
  font-weight: var(--font-weight-normal);
  line-height: 24px;
  letter-spacing: var(--letter-spacing-wider);
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
`;

const PracticeSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Label = styled.div`
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  margin-bottom: 20px;
`;

const PracticeButtonGroup = styled.div<{ numOptions: number }>`
  display: flex;
  max-width: 720px;
  height: 60px;
  background: var(--bg-card);
  border: 1px solid var(--interactive-primary);
  border-radius: 4px;
  overflow: hidden;
`;

const PracticeButton = styled.button<{ isSelected: boolean }>`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  background: ${props => props.isSelected ? 'var(--interactive-primary)' : 'transparent'};
  color: ${props => props.isSelected ? 'white' : 'var(--interactive-primary)'};
  font: inherit;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0;

  &:not(:last-child) {
    border-right: 1px solid var(--interactive-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: ${props => props.isSelected ? '#4ba3ae' : 'var(--bg-hover)'};
  }
`;

const PreferenceOptIn = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #f0f9fa;
  border-radius: 4px;
  border: 1px solid #c3d4e1;
  max-width: 720px;
  box-sizing: border-box;
`;

const CheckboxLabel = styled.label`
  cursor: pointer;
  flex: 1;
`;

const CheckboxLabelText = styled.div`
  font-weight: var(--font-weight-normal);
  margin-bottom: 4px;
  font-size: var(--font-size-xl);
  line-height: 24px;
  color: var(--text-primary);
  letter-spacing: var(--letter-spacing-wider);
`;

const CheckboxHint = styled.div`
  font-size: 12px;
  color: var(--text-primary);
  line-height: 16px;
`;

const StyledCheckbox = styled.input`
  margin-top: 2px;
  cursor: pointer;
  width: 18px;
  height: 18px;
  flex-shrink: 0;
`;

const PracticePatientSelector = ({ onClick, practice, patient }: Props) => {
  // Get onboarding context for suggestions
  const { userData } = useContext(OnboardingContext) || {};
  const hasSavedPrefs = localStorage.getItem('whata_remember_prefs') === 'true';
  
  // FIXED: Calculate suggestions IMMEDIATELY (not in useMemo)
  // This ensures they're available for state initialization
  const suggestions = (!hasSavedPrefs && userData?.practiceArea) 
    ? getInferredPreferences(userData)
    : null;

  // Initialize with suggestions OR current props (one time only)
  const [practiceInput, setPracticeInput] = useState<PracticeType>(() => {
    return suggestions?.practice || practice;
  });
  
  const [patientInput, setPatientInput] = useState<PatientType>(() => {
    return suggestions?.patient || patient;
  });
  
  // State for remember checkbox
  const [rememberPreferences, setRememberPreferences] = useState(() => {
    return localStorage.getItem('whata_remember_prefs') === 'true';
  });

  const handlePracticeSelect = (value: PracticeType) => {
    setPracticeInput(value);
  };

  const handlePatientOnChange = (value: string) =>
    setPatientInput(value as PatientType);

  // Handle remember checkbox changes
  const handleRememberChange = (checked: boolean) => {
    if (checked) {
      const prefsToSave = {
        practice: practiceInput,
        patient: patientInput
      };

      // Save to localStorage
      localStorage.setItem('whata_remember_prefs', 'true');
      localStorage.setItem('practice_patient', JSON.stringify(prefsToSave));

      // Update checkbox state
      setRememberPreferences(checked);
    } else {
      // Clear saved preferences when unchecked
      localStorage.removeItem('whata_remember_prefs');
      localStorage.removeItem('practice_patient');
      setRememberPreferences(checked);
    }
  };

  const practiceOptions = [
    { 
      value: Practice.PRIMARY,
      label: 'Community'
    },
    { 
      value: Practice.SECONDARY,
      label: 'Hospital'
    },
    { 
      value: Practice.SAP,
      label: 'Surgical Prophylaxis'
    }
  ];

  return (
    <div>
      <TitleContainer>
        <Title>Review antibiotic guidelines</Title>
        <SubTitle>
         Apply clinical judgement alongside these guidelines. For complex cases or scenarios not covered, seek ID/Micro advice. 
        </SubTitle>
      </TitleContainer>
      <InputWrapper>
        {/* Three-button practice selection (toggle style) */}
        <PracticeSection>
          <Label>Select where you prescribe antibiotics</Label>
          <PracticeButtonGroup numOptions={practiceOptions.length}>
            {practiceOptions.map(option => (
              <PracticeButton
                key={option.value}
                isSelected={practiceInput === option.value}
                onClick={() => handlePracticeSelect(option.value as PracticeType)}
                type="button"
              >
                {option.label}
              </PracticeButton>
            ))}
          </PracticeButtonGroup>
        </PracticeSection>

        {/* Keep existing patient toggle */}
        <ToggleInput
          label="Select patient type"
          defaultValue={patientInput}
          labelLeft={Patient.ADULT}
          valueLeft={Patient.ADULT}
          labelRight="Child"
          valueRight={Patient.CHILD}
          onChange={handlePatientOnChange}
        />

        {/* Existing remember preferences checkbox */}
        <PreferenceOptIn>
          <StyledCheckbox
            type="checkbox"
            id="remember-prefs"
            checked={rememberPreferences}
            onChange={(e) => handleRememberChange(e.target.checked)}
          />
          <CheckboxLabel htmlFor="remember-prefs">
            <CheckboxLabelText>
              Remember my selections
            </CheckboxLabelText>
            <CheckboxHint>
              Your practice and patient preferences will be saved on this device
            </CheckboxHint>
          </CheckboxLabel>
        </PreferenceOptIn>

        <Button
          onClick={() => {
            // Save preferences if checkbox is checked
            if (rememberPreferences) {
              const prefsToSave = {
                practice: practiceInput,
                patient: patientInput
              };
              localStorage.setItem('practice_patient', JSON.stringify(prefsToSave));
            }

            // IMPORTANT: Always call onClick with current selections
            // This updates the parent's state to match what we're saving
            onClick(practiceInput, patientInput);
          }}
          disabled={!practiceInput || !patientInput}
        >
          Next
        </Button>
      </InputWrapper>
    </div>
  );
};

export default PracticePatientSelector;